import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  fetchCompleteNewPassword,
  fetchCurrentCognitoUser,
  fetchLogin,
  fetchLogout
} from 'api/authApi';
import { parseUser } from 'services/userServices';
import { trackLoginEventGraphQL } from 'api/graphql/loginTrackingGraphQL';
import { clearForceLoginFlag, getForceLoginFlag } from 'utils/forceLoginRedirect';

export const InitialUserState = {
  id: '',
  username: '',
  email: '',
  firstName: '',
  lastName: '',
  customerId: null,
  stripeCustomerId: null
};

const initialState = {
  user: InitialUserState,
  isLoggedIn: false,
  isLoaded: false,
  isLoading: false
};

export const login = createAsyncThunk('auth/login', async ({ email, password }) => {
  try {
    clearForceLoginFlag();
    const user = await fetchLogin({ email, password });

    if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
      trackLoginEventGraphQL({ success: false, email }).catch(() => {});
      // eslint-disable-next-line
      return Promise.reject({ code: 'NEW_PASSWORD_REQUIRED' });
    }

    const parsedUser = parseUser(user.attributes);

    trackLoginEventGraphQL({ success: true, userId: parsedUser.id, email }).catch(() => {});

    return parsedUser;
  } catch (e) {
    trackLoginEventGraphQL({ success: false, email }).catch(() => {});
    return Promise.reject(e);
  }
});

export const logout = createAsyncThunk('auth/logout', async () => {
  try {
    localStorage.removeItem('hasCompany');

    await fetchLogout();

    return null;
  } catch (e) {
    return Promise.reject(e);
  }
});

export const currentUser = createAsyncThunk('auth/currentUser', async () => {
  // After forced session invalidation (e.g. admin email change), do not restore Amplify cache
  if (getForceLoginFlag()) {
    try {
      await fetchLogout();
    } catch {
      // ignore
    }
    return Promise.reject(new Error('Session expired'));
  }

  try {
    const user = await fetchCurrentCognitoUser();

    return parseUser(user.attributes);
  } catch (e) {
    return Promise.reject(e);
  }
});

export const completeNewPassword = createAsyncThunk(
  'auth/completeNewPassword',
  async ({ email, password, newPassword }) => {
    try {
      const cognitoUser = await fetchLogin({ email, password });
      const user = await fetchCompleteNewPassword({ cognitoUser, newPassword });

      return parseUser(user);
    } catch (e) {
      return Promise.reject(e);
    }
  },
);

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setIsLoggedIn(state, action) {
      state.isLoggedIn = action.payload;
    },
    setCustomerId(state, action) {
      state.user.customerId = action.payload;
    },
    updateUserProfile(state, action) {
      state.user = { ...state.user, ...action.payload };
    }
  },
  extraReducers: (builder) => {
    builder.addCase(login.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(login.fulfilled, (state, { payload }) => {
      state.isLoggedIn = true;
      state.isLoading = false;
      state.user = { ...state.user, ...payload };
    });
    builder.addCase(login.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(currentUser.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(currentUser.fulfilled, (state, { payload }) => {
      state.isLoggedIn = true;
      state.isLoaded = true;
      state.isLoading = false;
      state.user = { ...state.user, ...payload };
    });
    builder.addCase(currentUser.rejected, (state) => {
      state.isLoading = false;
      state.isLoaded = true;
    });
    builder.addCase(logout.pending, (state) => {
      state.isLoggedIn = false;
      state.isLoading = true;
    });
    builder.addCase(logout.fulfilled, (state) => {
      state.isLoggedIn = false;
      state.isLoading = false;
      state.user = InitialUserState;
    });
    builder.addCase(logout.rejected, (state) => {
      state.isLoading = false;
    });
  }
});

export const { setIsLoggedIn, setCustomerId, updateUserProfile } = authSlice.actions;

export default authSlice.reducer;
